import { InfiniteData } from '@tanstack/react-query';

import {
  TProgramBroadcastSegmentsParameters,
  TProgramBroadcastSummary,
} from '@/features/broadcast-schedule/types';
import {
  ClipSyncStatusEnum,
  ReviewStateEnum,
  TASRRecognitionResult,
  TBaseItemMention,
  TReviewStateValues,
} from '@/features/segments/types';
import { EReviewIncidenceType, TRect } from '@/types';
import { EVisibility } from '@/types/types';

enum EClipState {
  GENERATING = 'generating',
  GENERATED = 'generated',
  FAILED = 'failed',
}

export enum TimeKeepingReviewStateEnum {
  WITHOUT_REVIEW = 0,
  PENDING = 1,
  IN_REVIEW = 2,
  REVIEWED = 3,
  REJECTED = 4,
}

export enum ClipReviewStateEnum {
  REQUIRE_REVIEW = 0,
  PENDING = 1,
  IN_REVIEW = 2,
  REVIEWED = 3,
  REJECTED = 4,
  INCIDENCE = 5,
  WITHOUT_REVIEW = 6,
  NOT_REVIEWED = 7,
}

export type TClipTranscription = {
  id: string;
  clip_id: string;
  asr_recognition_results: TASRRecognitionResult[];
  created_at: string;
  created_by: string;
  updated_at: null;
  updated_by: null;
};

export type TPatchClipTranscription = {
  asr_recognition_results?: TASRRecognitionResult[] | null;
};

type TClip = {
  id: string;
  body: string;
  media_type: string;
  start_datetime: string;
  end_datetime: string;
  review_state: TReviewStateValues;
  program_id: string;
  program_broadcast_id?: string | null;
  /**
   * The emission this clip belongs to. A clip has at most one, so unlike a segment it
   * needs no ranking. Optional because a clip may have none bound to it.
   */
  program_broadcast?: TProgramBroadcastSummary | null;
  clip_category_id: string;
  created_at: string;
  current_user_id: string;
  clip_mentions: ClipMention[];
  clip_logos: TClipLogo[];
  clip_peoples: TClipPeople[];
  clip_declarations: TClipDeclaration[];
  clip_ocrs: TClipOcr[];
  clip_transcriptions: TClipTranscription[];
  reviewed_by_user_id?: string;
  review_incidence_type?: EReviewIncidenceType;
  review_incidence_note?: string;
  presigned_url?: string;
  audience?: number;
  economical_value?: number;
  audience_rating_percent?: number;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  state?: EClipState;
  sync_status: ClipSyncStatusEnum;
  parameters?: TProgramBroadcastSegmentsParameters;
  time_keeping_state: TimeKeepingReviewStateEnum;
  time_keeping_note?: string;
};

type TPostClip = {
  id?: string;
  body: string;
  media_type: string;
  start_datetime: string;
  end_datetime: string;
  duration_in_seconds: number;
  review_state: TReviewStateValues;
  clip_category_id: string;
  program_id: string;
  reviewed_by_user_id?: string;
  review_incidence_type?: EReviewIncidenceType;
  review_incidence_note?: string;
  time_keeping_state?: TimeKeepingReviewStateEnum;
};

type TPatchClip = {
  id: string;
  body?: string;
  uri?: string;
  channel_id?: string;
  media_type?: string;
  start_datetime?: Date;
  end_datetime?: Date;
  duration_in_seconds?: number;
  review_state?: TReviewStateValues;
  language_id?: string;
  clip_category_id?: string;
  program_id?: string;
  reviewed_by_user_id?: string;
  review_incidence_type?: EReviewIncidenceType;
  review_incidence_note?: string;
  time_keeping_state?: TimeKeepingReviewStateEnum;
  time_keeping_note?: string;
  silently?: boolean;
};

type TClipLogo = {
  kind?: 'logo';
  id?: string;
  segment_id?: string;
  entity_score: number;
  clip_id?: string;
  start_datetime: string;
  end_datetime: string;
  text?: string;
  parameters: TClipParameters;
  created_at?: string;
  entity_label?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  review_state: 0 | 1;
  logo_id: string;
  support?: string;
  children?: TClipLogo[];
  thumbnail_url?: string;
};
type TClipPeople = {
  kind?: 'people';
  id?: string;
  entity_score: number;
  clip_id?: string;
  start_datetime: string;
  end_datetime: string;
  text?: string;
  parameters: TClipParameters;
  created_at?: string;
  entity_label?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  review_state: 0 | 1;
  people_id: string;
  children?: TClipPeople[];
};
type TClipDeclaration = {
  clip_declaration_id?: string;
  people_id: string | null;
  id?: string;
  entity_score?: number;
  clip_id?: string;
  start_datetime: string;
  end_datetime: string;
  text: string;
  parameters?: TClipParameters;
  created_at?: string;
  entity_label?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  review_state: 0 | 1;
  children?: TClipDeclaration[];
};

type TClipParameters = {
  start_frame?: number;
  end_frame?: number;
  bbox: TRect;
  landmarks?: string[];
  segment_id?: string;
  visibility?: EVisibility;
  support_id?: string;
  is_manual?: boolean;
};

type ClipMention = TBaseItemMention & {
  id?: string;
  clip_id?: string;
  text?: string;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  clients?: string[];
  workspaces?: string[];
  is_tentative?: boolean;
  query_id?: string;
};

type TPostClipLogo = {
  segment_id?: string;
  clip_id: string;
  logo_id: string;
  entity_score: number;
  start_datetime: Date | null;
  end_datetime: Date | null;
  review_state: 0 | 1;
  parameters: TClipParameters;
  id?: string;
  silently?: boolean;
};

type TPatchClipLogo = {
  id: string;
  logo_id?: string;
  entity_score?: number;
  start_datetime?: Date | null;
  end_datetime?: Date | null;
  parameters?: TClipParameters;
  review_state?: 0 | 1;
  silently?: boolean;
};

type TPostClipPeople = {
  id?: string;
  clip_id: string;
  people_id: string | null;
  entity_score: number;
  start_datetime: Date | null;
  end_datetime: Date | null;
  review_state: 0 | 1;
  parameters: {
    bbox: TRect;
    segment_id?: string;
    visibility?: EVisibility;
    support_id?: string;
  };
  silently?: boolean;
};

type TPatchClipPeople = {
  id: string;
  people_id?: string | null;
  entity_score?: number;
  start_datetime?: Date | null;
  end_datetime?: Date | null;
  parameters?: TClipParameters;
  review_state?: 0 | 1;
  silently?: boolean;
};

type TPostClipDeclaration = {
  clip_id: string;
  text: string;
  start_datetime: Date | null;
  end_datetime: Date | null;
  people_id: string | null;
  entity_label: string | null;
  review_state: 0 | 1;
  entity_score: number;
  parameters?: {
    segment_id?: string;
  };
  silently?: boolean;
};

type TPatchClipDeclaration = {
  id?: string;
  text?: string;
  start_datetime?: Date | null;
  people_id?: string | null;
  review_state?: number;
  entity_label?: string | null;
  parameters?: {
    segment_id?: string;
  };
};

type TClipCategory = {
  id: string;
  name: string;
  created_at: string;
};

type TClipOcr = {
  id: string;
  clip_id: string;
  text: string;
  start_datetime: string;
  end_datetime: string;
  parameters: {
    hash: string;
  };
  created_at: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  review_state: 0 | 1;
};

type TPostClipOcr = {
  clip_id: string;
  text: string;
  start_datetime: Date | null;
  end_datetime: Date | null;
  review_state: 0 | 1;
};

type TPatchClipOcr = {
  id?: string;
  text?: string;
  start_datetime?: Date | null;
  end_datetime?: Date | null;
  review_state?: number;
};

type UpdateClipItem = {
  id: string;
  review_state: ReviewStateEnum;
};

export type TPostClipsBatch = {
  action: 'update';
  items: UpdateClipItem[];
};

export type {
  ClipMention,
  TClip,
  TClipCategory,
  TClipDeclaration,
  TClipLogo,
  TClipOcr,
  TClipParameters,
  TClipPeople,
  TPatchClip,
  TPatchClipDeclaration,
  TPatchClipLogo,
  TPatchClipOcr,
  TPatchClipPeople,
  TPostClip,
  TPostClipDeclaration,
  TPostClipLogo,
  TPostClipOcr,
  TPostClipPeople,
};

export { EClipState };

/** A page of an infinite clip list: a flat array, or the `{ data, total }` the cut lists keep. */
export type InfiniteClipsPage = TClip[] | { data: TClip[]; total: number };
export type InfiniteClips = InfiniteData<InfiniteClipsPage, number>;
